import React, { useEffect, useState, useRef, useContext } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON,
  ScaleControl,
} from "react-leaflet";
import L from "leaflet";
import "../../../static/styles/map.css";
import MapFlyTo from "./../components/MapFlyTo";
import FullScreenButton from "./../components/FullScreenButton";
import MouseIndicator from "./../components/MouseIndicator";
import WMSLayer from "./../components/WMSLayer/WMSLayer";
import LockMapButton from "./../components/LockMapButton";
import LeafletRuler from "./../components/LeafletRuler";
import DropdownMenu from "../components/DropdownMenu";
import { ObservationContext } from "./components/ObservationContext";
import {
  highlightFeature,
  canvasRenderer,
} from "../components/HightlightFeature";
import FileUpload from "../components/FileUpload";
import DownloadObservationButton from "../components/DownloadObservationButton";
import PlaceSearch from "../../components/PlaceSearch";

const ObservationMap = ({ AOIGeoJSON, pipesGeoJSON, observationData }) => {
  const [center, setCenter] = useState([25.153, 48.615]);
  const [isGeoJSONActive, setGeoJSONActive] = useState(true);
  const [isAOIActive, setAOIActive] = useState(false);
  const [isPipesActive, setPipesActive] = useState(true);
  const [WMSVisible, setWMSVisible] = useState(true);
  const [wmsLayer, setWmsLayer] = useState(null);
  const [isMapLocked, setIsMapLocked] = useState(false);
  const [zoom, setZoom] = useState(8);
  const mapRef = useRef(null);
  const { observationSelected } = useContext(ObservationContext);
  const [uploadedGeoJSON, setUploadedGeoJSON] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleLockToggle = (isLocked) => {
    setIsMapLocked(isLocked);
  };

  const buttons = [
    {
      type: "Slick",
      activeState: isGeoJSONActive,
      setActiveState: setGeoJSONActive,
    },
    { type: "AOI", activeState: isAOIActive, setActiveState: setAOIActive },
    {
      type: "Pipes",
      activeState: isPipesActive,
      setActiveState: setPipesActive,
    },
    { type: "Image", activeState: WMSVisible, setActiveState: setWMSVisible },
  ];

  const parseToSentinelhubDateRange = (fromDate) => {
    let date = new Date(fromDate);
    let plusTenMins = new Date(date.getTime() + 20 * 60000);
    let minusTenMins = new Date(date.getTime() - 20 * 60000);
    return [minusTenMins.toISOString(), plusTenMins.toISOString()];
  };

  const AOIStyle = (_) => ({
    color: "#3388ff",
    weight: 2,
    fillOpacity: 0,
    dashArray: "10,10",
  });

  const PipeStyle = (_) => ({
    color: "#3388ff",
    weight: 2,
    fillColor: "#ffffff",
    fillOpacity: 0,
  });

  const loadAcqLayer = (acquisition) => {
    if (acquisition && acquisition.wms_url) {
      let acq_time = acquisition.date;
      const timeRange = parseToSentinelhubDateRange(acq_time);
      let evalscript = "";

      if (acquisition.wms_layer === "SENTINEL-2-RGB") {
        evalscript = `
         //VERSION=3
        //This script was converted from v1 to v3 using the converter API
        //and sergio's  divine hand

        let minVal = 0.0;
        let maxVal = 0.4;

        let viz = new DefaultVisualizer(minVal, maxVal);

        function evaluatePixel(samples) {
            let val = [samples.B04, samples.B03, samples.B02, samples.dataMask];
            return val.map((v, i) => viz.process(v, i));
        }

        function setup() {
          return {
            input: [{
              bands: [
                "B02",
                "B03",
                "B04",
                "dataMask",
              ]
            }],
            output: {
              bands: 4
            }
          }
      }
  
      `;
      }

      const wmsLayer = L.tileLayer.wms(acquisition.wms_url, {
        tileSize: 512,
        transparent: true,
        format: "image/png",
        maxcc: 100,
        minZoom: 6,
        maxZoom: 25,
        layers: acquisition.wms_layer,
        time: timeRange[0] + "/" + timeRange[1],
        previewMode: "EXTENDED_PREVIEW",
        evalscript: btoa(evalscript),
      });
      setWmsLayer(wmsLayer);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (observationSelected.properties) {
      const centroidCoordinates =
        observationSelected.properties.centroid.coordinates;
      if (!isMapLocked) {
        setZoom(observationSelected.properties.zoom);
        setCenter([centroidCoordinates[1], centroidCoordinates[0]]);
      }
      if (process.env.NODE_ENV === "production") {
        loadAcqLayer(observationSelected.properties.acquisition_info);
      } else {
        console.warn("SENHUB LAYER DISABLED IN DEVELOPMENT MODE");
      }
    }
    setIsLoading(false);
  }, [observationSelected, isMapLocked]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.on("zoomend", () => {
        setZoom(mapRef.current.getZoom());
      });
    }
  }, [mapRef.current]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={`map-app-leak`} sx={{ position: "relative" }}>
      {(!observationSelected || observationSelected.length === 0) && (
        <Box
          sx={{
            width: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            right: "50%",
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1005,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
        >
          <Typography variant="h6">No observation selected</Typography>
        </Box>
      )}
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%", borderRadius: "15px" }}
        ref={mapRef}
      >
        <PlaceSearch setCenter={setCenter} setZoom={setZoom} />
        <MouseIndicator />
        <ScaleControl position="bottomleft" />
        <LeafletRuler />
        <MapFlyTo center={center} zoom={zoom} isFlyToEnabled={!isMapLocked} />

        <Box className="map-button-container">
          <FullScreenButton />
          <LockMapButton map={mapRef.current} onLockToggle={handleLockToggle} />
          <FileUpload
            onFileLoad={setUploadedGeoJSON}
            setCenter={setCenter}
            setZoom={setZoom}
          />
          <DownloadObservationButton dataSource={observationData} type="one" />
          <DropdownMenu buttons={buttons} />
        </Box>

        <LayersControl position="bottomright">
          <LayersControl.BaseLayer name="Relief" checked>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              minZoom={4}
              maxZoom={30}
              maxNativeZoom={17}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street Map">
            <TileLayer
              url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
              minZoom={4}
              maxZoom={30}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {AOIGeoJSON && isAOIActive && (
          <GeoJSON key={AOIGeoJSON.id} data={AOIGeoJSON} style={AOIStyle} />
        )}
        {pipesGeoJSON &&
          isPipesActive &&
          pipesGeoJSON.map((feature) => (
            <GeoJSON
              key={feature.id}
              data={{
                ...feature,
                object: "PIPE",
              }}
              style={PipeStyle}
              onEachFeature={highlightFeature}
              renderer={canvasRenderer}
            />
          ))}
        {observationSelected &&
          isGeoJSONActive &&
          observationSelected.geometry && (
            <GeoJSON key={observationSelected.id} data={observationSelected} />
          )}
        {wmsLayer && WMSVisible && (
          <Box>
            <WMSLayer wmsLayer={wmsLayer} isVisible={true} zIndex={1005} />
          </Box>
        )}
        {uploadedGeoJSON && (
          <GeoJSON
            data={uploadedGeoJSON}
            onEachFeature={highlightFeature}
            renderer={canvasRenderer}
          />
        )}
      </MapContainer>
    </Box>
  );
};

export default ObservationMap;
